import { useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from './redux-hook'
import { useSession } from 'next-auth/react'
import { ProgressStatus, ProgressVariant, SessionStatus, StateStatus, progressMap } from '../utils/client-constants'
import { fetchBaseConfigData } from '../redux/features/baseConfig/base-config-slice'
import { ensureSupportedBrowser, isValidUrl } from '../utils/utils'
import { usePathname } from 'next/navigation'
import Bowser from 'bowser'
import { login } from 'app/app/api/auth/auth-actions'
import { fetchMasterData } from '../redux/features/init/masterdata/masterDataSlice'
import { customerData as customerDataAction } from '../redux/features/customers/customer-slice'
import { getCustomerOpportunities } from '../redux/features/customers/customer-opportunity-async'
import { clearSfCache } from 'app/services/client/client-api'

export interface ProgressPayload {
  variant: ProgressStatus.LOADING | ProgressStatus.FAILURE | ProgressStatus.SUCCESS
  message: string
  isDataLoading: boolean
  value: number
  errorMessage?: string
  errorCode?: string | number
  errorStack?: string
}

export interface BrowserInfo {
  isCompatible: boolean
  name: string
  version: string
}

const {
  AUTHENTICATING,
  LOADING_BASE_CONFIG,
  LOADING_CUSTOMERS,
  LOADING_OPPORTUNITIES,
  MASTER_DATA_LOADING,
  ALL_DATA_LOADED
} = ProgressVariant
const { IDLE, LOADING, SUCCEEDED, FAILED } = StateStatus
export function useProgressInit() {
  const dispatch = useAppDispatch()
  const { status: sessionStatus } = useSession()
  const {
    status: masterDataStatus,
    error: masterDataError,
    errorDetails: masterDataErrorDetails
  } = useAppSelector(state => state.configdata)
  const {
    status: customerStatus,
    error: customerDataError,
    errorDetails: customerDataErrorDetails
  } = useAppSelector(state => state.customers)
  const { status: baseConfigStatus, error: baseConfigError } = useAppSelector(state => state.baseConfig)
  const isAppInitialized = useAppSelector(state => state.appInitialized.data)
  const pathname = usePathname()
  const [isBrowserSupported, setIsBrowserSupported] = useState(true)
  const [browserCheckComplete, setBrowserCheckComplete] = useState(false)
  const isValidPath = useMemo(() => isValidUrl(pathname), [pathname])
  const [browserInfo, setBrowserInfo] = useState<BrowserInfo>()

  // Ref to prevent double dispatch in React Strict Mode
  const hasDispatchedCustomerData = useRef(false)
  const hasDispatchedOpportunities = useRef(false)

  const getMessageForVariant = (variant: ProgressVariant) => progressMap.get(variant)?.message || ''

  const getValueForVariant = (variant: ProgressVariant) => progressMap.get(variant)?.value || 0

  const initialState: ProgressPayload = {
    message: getMessageForVariant(LOADING_BASE_CONFIG),
    variant: ProgressStatus.LOADING,
    value: getValueForVariant(LOADING_BASE_CONFIG),
    isDataLoading: true
  }

  const updateProgressState = useCallback(
    (
      progressVariant: ProgressVariant,
      variant?: ProgressStatus.LOADING | ProgressStatus.FAILURE,
      isDataLoading?: boolean,
      errorMessage?: string,
      errorCode?: string | number,
      errorStack?: string
    ) =>
      ({
        message: getMessageForVariant(progressVariant),
        variant,
        value: getValueForVariant(progressVariant),
        isDataLoading,
        errorMessage,
        errorCode,
        errorStack
      }) as ProgressPayload,
    []
  )

  const [progressState, setProgressStateLocal] = useState<ProgressPayload>(initialState)

  const setProgress = useCallback(
    (
      progressVariant: ProgressVariant,
      errorMessage?: string,
      isDataLoading?: boolean,
      errorCode?: string | number,
      errorStack?: string
    ) =>
      setProgressStateLocal(
        updateProgressState(
          progressVariant,
          errorMessage ? ProgressStatus.FAILURE : ProgressStatus.LOADING,
          isDataLoading,
          errorMessage,
          errorCode,
          errorStack
        )
      ),
    [updateProgressState]
  )

  const retry = useCallback(() => {
    clearSfCache()
      .catch(() => undefined)
      .finally(() => window.location.reload())
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const isSupported = ensureSupportedBrowser()
      setIsBrowserSupported(isSupported)
      setBrowserCheckComplete(true)
      if (!isSupported) {
        setBrowserInfo({
          isCompatible: false,
          name: Bowser.getParser(window.navigator.userAgent).getBrowserName(),
          version: Bowser.getParser(window.navigator.userAgent).getBrowserVersion() || ''
        })
      }
    }
  }, [])

  useEffect(() => {
    if (!isValidPath || !isBrowserSupported || isAppInitialized) {
      return
    }
    switch (baseConfigStatus) {
      case IDLE:
        if (browserCheckComplete) {
          dispatch(fetchBaseConfigData())
        }
        break
      case SUCCEEDED:
      case LOADING:
        setProgress(LOADING_BASE_CONFIG)
        break
      case FAILED:
        setProgress(LOADING_BASE_CONFIG, baseConfigError ?? undefined)
        break
    }
  }, [
    baseConfigStatus,
    dispatch,
    baseConfigError,
    isAppInitialized,
    isBrowserSupported,
    isValidPath,
    browserCheckComplete,
    setProgress
  ])

  useEffect(() => {
    if (!isValidPath || !isBrowserSupported || isAppInitialized || baseConfigStatus !== SUCCEEDED) {
      return
    }
    if (sessionStatus === SessionStatus.UNAUTHENTICATED) {
      login()
    }
    setProgress(
      sessionStatus === SessionStatus.LOADING || sessionStatus === SessionStatus.UNAUTHENTICATED
        ? AUTHENTICATING
        : MASTER_DATA_LOADING
    )
  }, [baseConfigStatus, sessionStatus, isAppInitialized, isValidPath, isBrowserSupported, setProgress])

  useEffect(() => {
    if (!isValidPath || !isBrowserSupported || isAppInitialized) {
      return
    }
    if (baseConfigStatus === SUCCEEDED && sessionStatus === SessionStatus.AUTHENTICATED && masterDataStatus === IDLE) {
      dispatch(fetchMasterData())
    }

    switch (masterDataStatus) {
      case SUCCEEDED:
        setProgress(LOADING_CUSTOMERS)
        break
      case FAILED:
        setProgress(
          MASTER_DATA_LOADING,
          masterDataError ?? undefined,
          undefined,
          masterDataErrorDetails?.code,
          masterDataErrorDetails?.stack
        )
        break
    }
  }, [
    baseConfigStatus,
    sessionStatus,
    masterDataStatus,
    masterDataError,
    masterDataErrorDetails,
    dispatch,
    isAppInitialized,
    isValidPath,
    isBrowserSupported,
    setProgress
  ])

  useEffect(() => {
    if (masterDataStatus === SUCCEEDED && customerStatus === IDLE && !hasDispatchedCustomerData.current) {
      hasDispatchedCustomerData.current = true
      dispatch(customerDataAction())
      return
    }

    if (customerStatus === SUCCEEDED) {
      setProgress(isAppInitialized ? ALL_DATA_LOADED : LOADING_OPPORTUNITIES)
      if (!isAppInitialized && !hasDispatchedOpportunities.current) {
        hasDispatchedOpportunities.current = true
        dispatch(getCustomerOpportunities())
      }
    } else if (customerStatus === FAILED) {
      setProgress(
        isAppInitialized ? ALL_DATA_LOADED : LOADING_OPPORTUNITIES,
        customerDataError ?? undefined,
        undefined,
        customerDataErrorDetails?.code,
        customerDataErrorDetails?.stack
      )
    }
  }, [
    customerStatus,
    customerDataError,
    customerDataErrorDetails,
    dispatch,
    isAppInitialized,
    masterDataStatus,
    setProgress
  ])

  return { progressState, browserInfo, retry }
}
